import {
  AnalyticsSearchQuery,
  HelmetCanonical,
  SearchResultStatus,
  OpportunitySearchResults,
  Pagination,
  ResultSearchTypeBreak,
  useSearchFilters,
  VolunteerSearchLink,
} from "@skv/core";
import {
  favouritesStore,
  opportunityResultStore,
  OpportunitySearchQueryState,
  useAppSettings,
  useOpportunitySearchQuery,
  useThunkDispatch,
} from "@skv/state";
import { FAVOURITES_ENUM } from "@skv/state/src/state/favourites";
import { locationSegmentsToReactNode } from "@skv/util";
import * as React from "react";
import { Helmet } from "react-helmet";
import { useHistory, useLocation } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { DefaultTheme } from "styled-components/dist/types";
import { useUserDispatch } from "~/state";
import { EmailSubscribeForm } from "./OpportunityEmailSubscribeForm";
import { OpportunitySearchResultsHeader } from "./OpportunitySearchResultsHeader";
import { OpportunityResultCard } from "./ResultCard";
import SearchFiltersSidebarForm from "./SearchFiltersSidebarForm";

type LocationType = {
  searchQuery?: ReturnType<typeof useOpportunitySearchQuery>;
  analytics?: {
    didKeywordAutoComplete: boolean;
    didWhereAutoComplete: boolean;
  };
};

export const SearchResults: React.FC<Record<string, never>> = () => {
  const history = useHistory();
  const location = useLocation<LocationType>();
  const { push } = history;
  const dispatch = useThunkDispatch();

  const wrapperRef = React.useRef(null);
  const searchResultsRef = React.useRef<HTMLDivElement | null>(null);

  const searchQuery = useOpportunitySearchQuery();
  const { applicationUrl, applicationName, showFiltersInSearch } =
    useAppSettings();

  const { data: searchResults, state } =
    opportunityResultStore.hooks.useOpportunitySearchResult(searchQuery);

  const { filters, handleFiltersChange } = useSearchFilters({
    searchQuery,
    onChange: (nextQuery) => {
      dispatch(
        opportunityResultStore.actions.fetchOpportunities(nextQuery)
      ).then((data) => {
        push(data.canonicalUrl, {
          searchQuery: nextQuery,
          analytics: location.state?.analytics,
        });
      });
    },
  });

  const handleReset = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    e.preventDefault();
    dispatch(opportunityResultStore.actions.fetchOpportunities()).then(
      (data) => {
        push(data.canonicalUrl, {
          searchQuery: {},
          analytics: location.state?.analytics,
        });
      }
    );
  };

  const analytics = React.useMemo(() => {
    return (
      <AnalyticsSearchQuery
        searchQuery={searchQuery}
        data={{
          subSection: "opportunity",
          subSubSection: "opportunity:search+results",
          subSubSubSection: `opportunity:search+results:page${searchResults?.page}`,
          pageTitle: `opportunity:search+results:page${searchResults?.page}`,
          pageType: "opportunity:search+results",
          searchLocationInputMethod: location.state?.analytics
            ?.didWhereAutoComplete
            ? "auto-suggest"
            : "typed",
          searchTermInputMethod: location.state?.analytics
            ?.didKeywordAutoComplete
            ? "auto-suggest"
            : "typed",
        }}
      />
    );
  }, [searchQuery, searchResults, location.state]);

  React.useEffect(() => {
    /* on server load, set current searchQuery to history state if no current searchQuery */
    if (!location.state?.searchQuery) {
      history.replace(history.location.pathname + history.location.search, {
        searchQuery,
        ...location.state,
      });
    }

    const handlePopState = (e: PopStateEvent): void => {
      dispatch(
        opportunityResultStore.actions.fetchOpportunities(
          e.state.state?.searchQuery
        )
      );
    };

    window.addEventListener("popstate", handlePopState, false);

    return (): void => {
      window.removeEventListener("popstate", handlePopState, false);
    };
  }, []);

  React.useEffect(() => {
    /**
     * if at any time a user refreshes the page, the cache clears but their history stack is still there
     * if they navigate their history via browser back/forward, and hit the search results view again,
     * we will be in the below state
     */
    if (state === SearchResultStatus.IDLE && !searchResults) {
      dispatch(opportunityResultStore.actions.fetchOpportunities(searchQuery));
    }
  }, [state, searchResults, dispatch, searchQuery]);

  const { category, keyword, where, commitment } = searchQuery || {};

  React.useEffect(() => {
    const hasQueries = !!(
      category?.length ||
      keyword ||
      where ||
      commitment?.length
    );

    if (hasQueries && searchResultsRef.current) {
      searchResultsRef.current.scrollIntoView({
        behavior: "auto",
        block: "start",
      });
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [category, keyword, where, commitment, searchResultsRef]);

  return (
    <>
      <Helmet>
        <title>
          {searchResults?.pageTitle || "Volunteer in New South Wales"}
        </title>
        <meta
          property="og:title"
          content={`${
            searchResults?.pageTitle || "Volunteer in New South Wales"
          } | ${applicationName}`}
        />
        <meta
          name="description"
          content={
            searchResults?.pageDescription ||
            "Find volunteer opportunities in New South Wales"
          }
        />
        <meta
          property="og:description"
          content={
            searchResults?.pageDescription ||
            "Find volunteer opportunities in New South Wales"
          }
        />
      </Helmet>

      {searchResults?.noLocationMatch ||
      !searchResults?.totalCount ||
      searchResults?.page > 1 ? (
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      ) : (
        <HelmetCanonical
          url={`${applicationUrl}${searchResults?.canonicalUrl}`}
        />
      )}

      <section ref={wrapperRef}>
        <div className="row">
          {/* Sidebar Filters */}
          {showFiltersInSearch ? null : (
            <aside className="col-lg-3 mb-4 mb-lg-0">
              <SearchFiltersSidebarForm
                searchQuery={searchQuery}
                filters={filters}
                onChange={handleFiltersChange}
                resetLinkTo={{ pathname: "/volunteering", state: {} }}
                onReset={handleReset}
              />
            </aside>
          )}
          {/* Results */}
          <section
            className={showFiltersInSearch ? "col-12" : "col-lg-9"}
            css={
              !showFiltersInSearch &&
              `
                @media (min-width: ${(p): string => p.theme.mdBreakpoint}) {
                  padding-left: 4rem;
                }
              `
            }
          >
            <div ref={searchResultsRef}>
              <SearchResultsMain
                state={state}
                results={searchResults}
                searchQuery={searchQuery}
                location={location}
              />
            </div>
            {state === SearchResultStatus.IDLE && analytics}
          </section>
        </div>
      </section>
    </>
  );
};

interface SearchResultsMainProps {
  state: SearchResultStatus;
  searchQuery: OpportunitySearchQueryState;
  results: OpportunitySearchResults;
  location: ReturnType<typeof useLocation<LocationType>>;
}

const SearchResultsMain = (props: SearchResultsMainProps) => {
  const { updateUser } = useUserDispatch();
  const { favourites, handleFavourite, handleUnfavourite } =
    favouritesStore.hooks.useFavourites();

  if (props.state === SearchResultStatus.FETCH_ERROR) {
    return (
      <p role="status">
        <strong>Sorry. Something went wrong.</strong>
        <br />
        Try reloading the page, or changing your search criteria.
      </p>
    );
  }

  if (props.state === SearchResultStatus.FETCHING_DATA) {
    return (
      <p role="status" className="mt-3">
        Please wait. Loading…
      </p>
    );
  }

  // SearchResultStatus.IDLE & HasOpportunities
  if (props.results?.opportunities && props.results.opportunities.length) {
    return (
      <>
        <div className="my-4">
          <OpportunitySearchResultsHeader
            searchQuery={props.searchQuery}
            searchResults={props.results}
          />
        </div>

        <EmailSubscribeForm
          id="topEmailSubscribe"
          searchQuery={props.searchQuery}
          className="d-none d-lg-block"
        />

        <ul
          className="list-unstyled"
          css={`
            li:first-of-type > article {
              border-top: 1px solid ${(p) => p.theme.gray300};
            }
          `}
        >
          {props.results.opportunities.map((opp, idx) => {
            const resultNumber =
              (props.results?.page - 1) * props.results?.pageSize + (idx + 1);

            return (
              <React.Fragment key={opp.id}>
                {props.searchQuery.where &&
                  props.results?.surroundingOpportunityIndex !== 0 &&
                  resultNumber ===
                    props.results?.surroundingOpportunityIndex + 1 && (
                    <li className="text-center pt-4 pb-3">
                      <ResultSearchTypeBreak
                        opportunitySearchType={
                          props.results?.opportunitySearchType
                        }
                      />
                    </li>
                  )}
                <li className="my-auto">
                  <ThemeProvider
                    theme={(theme) => ({
                      ...theme,
                      dangerColor: (theme as DefaultTheme).primaryColor,
                    })}
                  >
                    <OpportunityResultCard
                      name={opp.name}
                      to={{
                        pathname: opp.url,
                        state: {
                          referer: props.location,
                        },
                      }}
                      headingProps={{ className: "h1" }}
                      subHeadingProps={{ className: "h3" }}
                      isNew={opp.isNew}
                      isFeatured={opp.isFeatured}
                      eventStartDate={opp.eventStartDate ?? undefined}
                      eventEndDate={opp.eventEndDate ?? undefined}
                      isFavourited={!!favourites.opportunities[opp.id]}
                      onFavourite={() => {
                        handleFavourite(opp.id, FAVOURITES_ENUM.OPPORTUNITY);
                        updateUser((prev) => ({
                          ...prev,
                          favouriteCount: prev.favouriteCount + 1,
                        }));
                      }}
                      onUnfavourite={() => {
                        handleUnfavourite(opp.id, FAVOURITES_ENUM.OPPORTUNITY);
                        updateUser((prev) => ({
                          ...prev,
                          favouriteCount: prev.favouriteCount - 1,
                        }));
                      }}
                      organisationName={opp.organisation.name}
                      organisationTo={{
                        pathname: opp.organisation.url,
                        state: {
                          referer: props.location,
                        },
                      }}
                      logoUrl={opp.organisation.logoUrl}
                      locations={locationSegmentsToReactNode(
                        opp.locations,
                        opp.onlineRemoteVolunteers
                      )}
                      category={
                        opp.category ? (
                          <VolunteerSearchLink
                            className="font-weight-normal text-dark"
                            to={opp.category.url}
                            searchQuery={{
                              category: [opp.category.value],
                            }}
                            searchType="opp"
                            aria-label={`Category: ${opp.category.name}`}
                          >
                            {opp.category.name}
                          </VolunteerSearchLink>
                        ) : null
                      }
                      typesOfWork={
                        opp.typesOfWork.length ? (
                          <>
                            {opp.typesOfWork.map((type, idx) => (
                              <React.Fragment key={idx}>
                                {type.url ? (
                                  <VolunteerSearchLink
                                    className="font-weight-normal text-dark"
                                    to={type.url}
                                    searchQuery={{
                                      typeofwork: [type.value],
                                    }}
                                    searchType="opp"
                                  >
                                    {type.name}
                                  </VolunteerSearchLink>
                                ) : (
                                  <span>{opp.name}</span>
                                )}
                                {idx < opp.typesOfWork.length - 1 && (
                                  <span>, </span>
                                )}
                              </React.Fragment>
                            ))}
                          </>
                        ) : null
                      }
                      summary={opp.summary}
                    />
                  </ThemeProvider>
                </li>
              </React.Fragment>
            );
          })}
        </ul>

        <EmailSubscribeForm
          id="bottomEmailSubscribe"
          searchQuery={props.searchQuery}
        />

        <Pagination
          isNsw={true}
          currentPage={props.results?.page}
          totalPages={props.results?.totalPages}
          searchQuery={props.searchQuery}
          searchType="opp"
        />
      </>
    );
  }

  // SearchResultStatus.IDLE & NO RESULTS
  if (props.results?.totalCount === 0) {
    return (
      <div className="mt-3">
        <p>
          Sorry, we couldn't find any opportunities that matched what you were
          searching for.
          <br />
          Maybe try the following...
        </p>
        <ul>
          <li>Change or reset the filters to broaden your search</li>
          <li>Check the spelling of your search terms</li>
          {/* <li>Choose a wider search radius, e.g. + 50km</li> */}
        </ul>
      </div>
    );
  }

  return null;
};
